import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Loader2, AlertTriangle, RefreshCw, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Detailed progress modal for Gate 6 TTS generation
 * Shows real-time segment progress, cost, time estimates
 * Used by StreamlinedUploadFlow for production upload
 * 
 * IMPORTANT: Handles hard failures where 0 segments are generated
 * STALL DETECTION: Shows warning after 10+ minutes of no progress (increased from 3 min to reduce false alarms)
 * PHASE 3: Shows disabled "Close and Create Manuscript" button during stalls
 *          (Button will be enabled in Phase 4)
 */
const StreamlinedTTSProgressModal = ({ 
  isOpen,
  bookTitle,
  progress, // Full progress object from backend
  onContinue,
  onRetry, // Callback to retry/resume TTS generation
  onCloseWithPartial, // PHASE 4: Callback to close with partial progress
  error = null
}) => {
  // Stall detection state
  const lastProgressTimeRef = React.useRef(0);
  const lastCompletedCountRef = React.useRef(0);
  const [isStalled, setIsStalled] = useState(false);
  const [stallDurationMinutes, setStallDurationMinutes] = useState(0);
  const [isClosingWithPartial, setIsClosingWithPartial] = useState(false); // PHASE 4: Track button state
  
  // Stall detection: If no progress for 10 minutes while in_progress, show warning
  // Increased from 3 to 10 minutes to reduce false alarms (some segments just take longer)
  const STALL_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes
  
  // Stall detection effect - runs on interval
  useEffect(() => {
    if (!progress) return;
    
    // Initialize time ref if not set
    if (lastProgressTimeRef.current === 0) {
      lastProgressTimeRef.current = Date.now();
    }
    
    // Track completed count changes and reset timer
    const currentCompleted = progress.completed_count || 0;
    if (currentCompleted > lastCompletedCountRef.current) {
      lastProgressTimeRef.current = Date.now();
      lastCompletedCountRef.current = currentCompleted;
      setIsStalled(false);
      setStallDurationMinutes(0);
    }
    
    // Set up interval to check for stall
    const intervalId = setInterval(() => {
      const timeSinceProgress = Date.now() - lastProgressTimeRef.current;
      const isProcessing = progress.status === 'in_progress';
      const hasPending = (progress.pending_count || 0) > 0;
      
      // Calculate stall duration in minutes
      const stallMinutes = Math.floor(timeSinceProgress / 60000);
      setStallDurationMinutes(stallMinutes);
      
      setIsStalled(isProcessing && hasPending && timeSinceProgress > STALL_THRESHOLD_MS);
    }, 10000); // Check every 10 seconds
    
    return () => clearInterval(intervalId);
  }, [progress, STALL_THRESHOLD_MS]);
  
  // Handle loading/initializing state
  // Backend returns: { status, total_segments, completed_count, pending_count, stats: {...}, tts: {...} }
  // Check for total_segments at root level (not inside stats)
  if (!progress || progress.total_segments === undefined) {
    return (
      <Dialog open={isOpen} onOpenChange={() => {}}>
        <DialogContent 
          className="sm:max-w-lg bg-slate-900 border-slate-700 text-white"
          hideCloseButton={true}
        >
          <div className="text-center p-6">
            <Loader2 className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
            <h3 className="text-xl font-semibold mb-3">Starting Audio Generation</h3>
            <p className="text-slate-400">Preparing your manuscript for audio generation...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Backend response structure - fields are at root level
  const { 
    status, 
    total_segments, 
    completed_count, 
    pending_count, 
    failed_count,
    current_segment,
    current_segment_heading,  // NEW: Friendly chapter name
    stats = {},
    tts = {},
    fail_reason,
    last_error,
    // NEW: Batch tracking fields
    current_batch = 0,
    last_completed_batch = 0,
    total_batches = 0,
    current_batch_size = 0,
    // Segment titles for display
    segment_titles = {}
  } = progress;
  
  const totalSegments = total_segments || 0;
  const percentComplete = totalSegments > 0 ? (completed_count / totalSegments) * 100 : 0;
  const timeRemainingSeconds = (pending_count || 0) * 15; // Estimate 15 seconds per segment
  const timeRemainingMinutes = Math.ceil(timeRemainingSeconds / 60);
  
  // Determine if we're in a batch delay period
  const isWaitingBetweenBatches = current_batch > 0 && 
    last_completed_batch === current_batch && 
    current_batch < total_batches;
  
  // Detect different failure states
  const effectiveFailReason = fail_reason || tts.fail_reason;
  const effectiveLastError = last_error || tts.last_error;
  
  // CRITICAL: Check for hard failure - 0 segments completed is a HARD FAIL
  const isHardFail = 
    status === 'failed' || 
    tts.state === 'FAIL' || 
    effectiveFailReason === 'NO_AUDIO_GENERATED' ||
    (totalSegments > 0 && completed_count === 0 && (status === 'completed_with_failures' || status === 'completed'));
  
  // STRICT COMPLETION CHECK: Must have ALL segments completed with no failures
  // Don't show "complete" if we're still missing segments
  const isTrulyComplete = (
    !isHardFail && 
    (status === 'completed' || status === 'completed_with_failures') &&
    completed_count === totalSegments &&
    completed_count > 0
  );
  
  // Partial completion: backend says done but we're missing segments
  const isPartialComplete = (
    !isHardFail &&
    (status === 'completed' || status === 'completed_with_failures') &&
    completed_count > 0 &&
    completed_count < totalSegments
  );
  
  const isComplete = isTrulyComplete;
  const isFailed = status === 'failed' || isHardFail;
  const isProcessing = !isComplete && !isFailed && !isStalled && !isPartialComplete;

  // Get human-readable error message
  const getErrorMessage = () => {
    if (effectiveFailReason === 'NO_AUDIO_GENERATED') {
      return 'TTS failed: No audio was generated. This could be due to an OpenAI API issue or R2 storage problem.';
    }
    if (effectiveLastError) {
      return `Error: ${effectiveLastError.message || effectiveLastError.code || 'Unknown error'}`;
    }
    return 'TTS generation failed unexpectedly. Please try again.';
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-lg bg-slate-900 border-slate-700 text-white max-h-[90vh] overflow-hidden flex flex-col"
        hideCloseButton={!isComplete && !isFailed}
      >
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          <h2 className="text-xl font-bold mb-4 text-center text-white">
            TTS Processing{bookTitle ? `: "${bookTitle}"` : ''}
          </h2>
          
          {/* Error State */}
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 rounded-lg border border-red-700">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}
          
          {/* HARD FAIL State - 0 segments generated */}
          {isHardFail && (
            <div className="mb-4 p-3 bg-red-900/30 rounded-lg border border-red-700">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <p className="text-red-300 font-semibold text-sm">TTS Generation Failed</p>
              </div>
              <p className="text-slate-300 text-xs mb-2">
                {getErrorMessage()}
              </p>
              {effectiveFailReason && (
                <p className="text-slate-500 text-xs font-mono">
                  Error: {effectiveFailReason}
                </p>
              )}
              <div className="mt-2 p-2 bg-slate-800/50 rounded text-xs text-slate-400">
                <p className="font-medium mb-1">Try:</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>Check OpenAI API key</li>
                  <li>Verify R2 credentials</li>
                  <li>Click &quot;Retry TTS&quot;</li>
                </ul>
              </div>
            </div>
          )}
          
          {/* STALLED State - No progress for 10+ minutes */}
          {isStalled && !isHardFail && !isFailed && (
            <div className="mb-4 p-3 bg-amber-900/20 rounded-lg border border-amber-600/50">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <p className="text-amber-300 font-semibold text-sm">Processing Slower Than Expected</p>
              </div>
              <p className="text-slate-300 text-xs">
                No progress for {stallDurationMinutes}min • {completed_count}/{totalSegments} segments done
              </p>
              <p className="text-slate-400 text-xs mt-1">
                Could be a slow segment or a stalled job. Wait, resume, or save progress.
              </p>
              
              {/* Compact Save Button */}
              <div className="mt-3 pt-2 border-t border-amber-700/30">
                <Button
                  disabled={isClosingWithPartial || !onCloseWithPartial}
                  onClick={async () => {
                    if (onCloseWithPartial) {
                      setIsClosingWithPartial(true);
                      try {
                        await onCloseWithPartial();
                      } finally {
                        setIsClosingWithPartial(false);
                      }
                    }
                  }}
                  className={`w-full text-sm py-2 ${
                    isClosingWithPartial 
                      ? 'bg-slate-600 text-slate-400 cursor-wait'
                      : 'bg-amber-700 hover:bg-amber-600 text-white'
                  }`}
                  title="Save progress and continue to manuscript page"
                  data-testid="partial-close-btn"
                >
                  {isClosingWithPartial ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                      Saving...
                    </>
                  ) : (
                    'Save Progress & Continue Later'
                  )}
                </Button>
              </div>
            </div>
          )}
          
          {/* Partial Failure State - Show prominently */}
          {isFailed && !isHardFail && (
            <div className="mb-4 p-3 bg-amber-900/30 rounded-lg border border-amber-600">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <p className="text-amber-300 font-semibold text-sm">TTS Generation Paused</p>
              </div>
              <p className="text-slate-300 text-xs">
                Process stopped unexpectedly. 
                {completed_count > 0 && ` ${completed_count} segments completed.`}
                {pending_count > 0 && ` ${pending_count} remaining.`}
              </p>
              <p className="text-slate-400 text-xs mt-1">
                Click &quot;Resume TTS&quot; to continue from where it left off.
              </p>
            </div>
          )}
          
          {/* Segment Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="font-medium text-slate-300">Progress</span>
              <span className="text-slate-400">
                {completed_count} / {totalSegments} segments
              </span>
            </div>
            
            <div className="w-full bg-slate-700 rounded-full h-4 overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  isHardFail ? 'bg-red-600' : 'bg-blue-600'
                }`}
                style={{ width: `${percentComplete}%` }}
              />
            </div>
            
            <div className="text-center mt-1">
              <span className={`text-xl font-bold ${isHardFail ? 'text-red-400' : 'text-blue-400'}`}>
                {percentComplete.toFixed(0)}%
              </span>
            </div>
          </div>
          
          {/* Current Segment with Chapter Heading (only show if processing) */}
          {!isComplete && !isFailed && (current_segment || current_segment_heading) && (
            <div className="mb-4 p-3 bg-blue-900/30 rounded-lg border-l-4 border-blue-500">
              <div className="flex items-center gap-2">
                {/* Processing Spinner */}
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent flex-shrink-0" />
                
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-blue-300 text-sm truncate">
                    {current_segment_heading || segment_titles[current_segment] || `Segment ${completed_count + 1}`}
                  </p>
                  <p className="text-xs text-slate-500">
                    Segment {completed_count + 1} of {totalSegments}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Batch Progress Info (compact) */}
          {total_batches > 0 && (
            <div className="mb-4 space-y-1">
              <div className="flex justify-between items-center text-xs">
                <span className="text-slate-400">Batch:</span>
                <span className="text-slate-300">
                  {current_batch || last_completed_batch}/{total_batches}
                  {last_completed_batch < current_batch && " (processing)"}
                </span>
              </div>
              
              {/* Inter-batch delay indicator */}
              {isWaitingBetweenBatches && (
                <div className="p-2 bg-yellow-900/30 rounded border border-yellow-700/50 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 text-yellow-500 animate-spin" />
                  <span className="text-xs text-yellow-400">
                    Waiting before next batch...
                  </span>
                </div>
              )}
            </div>
          )}
          
          {/* Stats Grid (compact) */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="text-center p-2 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className={`text-xl font-bold ${completed_count > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {completed_count}
              </p>
              <p className="text-xs text-slate-400">Done</p>
            </div>
            <div className="text-center p-2 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xl font-bold text-slate-400">{pending_count || 0}</p>
              <p className="text-xs text-slate-400">Left</p>
            </div>
            <div className="text-center p-2 bg-slate-800/50 rounded-lg border border-slate-700">
              <p className="text-xl font-bold text-blue-400">
                ${((stats.total_cost_cents || 0) / 100).toFixed(2)}
              </p>
              <p className="text-xs text-slate-400">Cost</p>
            </div>
          </div>
          
          {/* Time Estimate (only show if not complete) */}
          {!isComplete && !isFailed && timeRemainingMinutes > 0 && (
            <div className="text-center mb-4 p-2 bg-amber-900/30 rounded-lg border border-amber-700">
              <p className="text-xs text-amber-300">
                ⏱️ ~{timeRemainingMinutes} {timeRemainingMinutes === 1 ? 'min' : 'mins'} remaining
              </p>
            </div>
          )}
          
          {/* Failed Segments Alert */}
          {(failed_count || 0) > 0 && !isHardFail && (
            <div className="mb-4 p-2 bg-red-900/30 rounded-lg border-l-4 border-red-500">
              <p className="text-red-300 text-xs font-medium">
                ⚠️ {failed_count} segment(s) failed
              </p>
            </div>
          )}
          
          {/* Complete State */}
          {isComplete && (
            <div className="mb-4 p-3 bg-green-900/30 rounded-lg border border-green-700 text-center">
              <p className="text-green-300 font-medium">
                ✓ Audio generation complete!
              </p>
            </div>
          )}
          
          {/* Buttons - Show different buttons based on state */}
          <div className="space-y-2">
            {/* Resume/Retry Button - DISABLED: Use Close and Create Manuscript instead */}
            {/* The force-resume endpoint has reliability issues - Close button is the safer option */}
            {false && (isFailed || isStalled) && onRetry && (
              <Button
                onClick={onRetry}
                className="w-full py-2 px-4 rounded-lg font-medium bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="tts-retry-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {isHardFail 
                  ? 'Retry TTS Generation' 
                  : isStalled 
                  ? `Resume TTS (${pending_count} segments remaining)`
                  : `Resume TTS (${pending_count} segments remaining)`}
              </Button>
            )}
            
            {/* Continue Button - BLOCKED when hard fail, stalled, or incomplete */}
            <Button
              onClick={onContinue}
              disabled={isHardFail || isProcessing || (isPartialComplete && pending_count > 0)}
              className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
                isComplete
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : isPartialComplete
                  ? 'bg-amber-700 hover:bg-amber-600 text-white border border-amber-500'
                  : (isFailed && !isHardFail) || isStalled
                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-300 border border-slate-600'
                  : 'bg-slate-700 text-slate-400 cursor-not-allowed'
              }`}
              data-testid="tts-progress-continue-btn"
            >
              {isComplete 
                ? 'Continue to Title Page' 
                : isPartialComplete
                ? `Continue with ${completed_count}/${totalSegments} segments`
                : isStalled
                ? 'Skip & Continue with Partial Audio'
                : isFailed && !isHardFail
                ? 'Skip & Continue with Partial Audio'
                : isHardFail
                ? 'Cannot Continue - No Audio Generated'
                : 'Processing...'}
            </Button>
            
            {/* Partial completion warning */}
            {isPartialComplete && (
              <div className="flex items-center justify-center gap-2 text-xs text-amber-400">
                <AlertTriangle className="w-3 h-3" />
                <span>Warning: {totalSegments - completed_count} segment(s) did not complete. Audio may be incomplete.</span>
              </div>
            )}
            
            {/* Hard fail warning */}
            {isHardFail && (
              <div className="flex items-center justify-center gap-2 text-xs text-red-400">
                <AlertCircle className="w-3 h-3" />
                <span>You must retry TTS before continuing</span>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StreamlinedTTSProgressModal;
